import { StintAverageRow } from '../types';

/**
 * Full lap history per team, kept client-side.
 *
 * Laps used to ride along with every frame; they now arrive once each as
 * `teamLapAppended` and are accumulated here. Storage is
 * `teamId -> stintIndex -> lapIndex -> seconds`, addressed rather than appended,
 * so applying the same lap twice is a no-op — which is what makes a replay's
 * backwards seek safe.
 */
export type LapHistory = Map<string, Map<number, Map<number, number>>>;

export interface AppendedLap {
  teamId: string;
  stintIndex: number;
  lapIndex: number;
  seconds: number;
}

export function emptyLapHistory(): LapHistory {
  return new Map();
}

export function appendLap(history: LapHistory, lap: AppendedLap): void {
  const { teamId, stintIndex, lapIndex, seconds } = lap;
  if (!Number.isFinite(seconds) || seconds <= 0) return;

  let stints = history.get(teamId);
  if (!stints) {
    stints = new Map();
    history.set(teamId, stints);
  }

  let laps = stints.get(stintIndex);
  if (!laps) {
    laps = new Map();
    stints.set(stintIndex, laps);
  }

  laps.set(lapIndex, seconds);
}

/**
 * Seed from a frame that still carries `lapTimesSeconds`.
 *
 * Only recordings made before laps moved onto their own event do that, so this
 * is what keeps those events replayable with full lap detail.
 */
export function seedFromStints(
  history: LapHistory,
  teamId: string,
  stints: StintAverageRow[],
): void {
  const stored = history.get(teamId);

  stints.forEach((stint, stintIndex) => {
    const laps = stint.lapTimesSeconds;
    if (!laps?.length) return;

    // Legacy frames repeat the whole history every time, so seeding naively
    // would be O(laps) per frame — the very cost this change exists to remove.
    // Once a stint is fully stored there is nothing new in it.
    if (stored?.get(stintIndex)?.size === laps.length) return;

    laps.forEach((seconds, lapIndex) =>
      appendLap(history, { teamId, stintIndex, lapIndex, seconds }),
    );
  });
}

/**
 * Laps per stint at the position described by `stints`.
 *
 * The stint rows are the authority on how many laps have actually happened, so
 * anything beyond their `lapCount` is dropped. After seeking backwards the store
 * still holds laps from further ahead in the race; without this they would show
 * up as laps that have not been run yet.
 */
export function stintLaps(
  history: LapHistory,
  teamId: string,
  stints: StintAverageRow[],
): number[][] {
  const byStint = history.get(teamId);
  if (!byStint) return stints.map(() => []);

  return stints.map((stint, stintIndex) => {
    const laps = byStint.get(stintIndex);
    if (!laps) return [];

    const ordered: number[] = [];
    for (let i = 0; i < stint.lapCount; i += 1) {
      const seconds = laps.get(i);
      if (seconds !== undefined) ordered.push(seconds);
    }
    return ordered;
  });
}

/** Every lap the team has run, oldest first, flattened across stints. */
export function allLaps(
  history: LapHistory,
  teamId: string,
  stints: StintAverageRow[],
): number[] {
  return stintLaps(history, teamId, stints).flat();
}

export interface TeamLapHistorySnapshot {
  teamId: string;
  stints: number[][];
}

/**
 * Apply the catch-up snapshot a client receives on connect, covering laps run
 * before it joined. Addressed like every other write, so it merges cleanly with
 * laps that have already streamed in.
 */
export function seedFromSnapshot(
  history: LapHistory,
  snapshot: TeamLapHistorySnapshot[],
): void {
  for (const { teamId, stints } of snapshot) {
    stints.forEach((laps, stintIndex) => {
      laps.forEach((seconds, lapIndex) =>
        appendLap(history, { teamId, stintIndex, lapIndex, seconds }),
      );
    });
  }
}
